import { forEach } from 'lodash';
import type { MapGeoJSONFeature } from 'react-map-gl/mapbox';
import type { TOC as TOCType } from '~/common/types';
import { REPORT_ALL_ID } from '~/common/types';
import { getNearestPresetColor } from '~/constants/color';
import { customDataCategoryLabel } from '~/pages/projectDetails/map/customDataLayers.helpers';
import type { MapboxSourceConfig, TilesConfig } from '~/store2/layers.data';
import { isSupportedMetadataLayer } from '~/store2/TOCLayers';
import {
  customDataFeatureLayerId,
  customDataLayerLabel,
  deriveCategoryLabel,
  EXCLUDED_FIELDS,
  formatFieldRow,
  formatParcelField,
  isCustomDataFeature,
  isParcelFeature,
  normalizeZip,
  ZIP_FIELD_KEYS,
} from '../IdentifyDetails/common';

export interface SidebarFieldRow {
  label: string;
  value: string;
  isUrl: boolean;
  // True when the raw value was empty (null/''/'Not Available'); `value` is then an
  // em-dash and the row is clustered to the bottom of its section.
  isEmpty: boolean;
  // `${layerId}.${fieldId}` — matches layerStore.visibleMetadataIds membership key.
  uniqueId: string;
  eyeSupported: boolean;
}

// Identifies the live vector-tile feature behind a section, so the highlight can be driven by
// mapbox feature-state (every loaded tile fragment paints) instead of one clipped geometry.
// Undefined when the tile feature carries no id — custom data, or a layer with no OID field.
export interface FeatureRef {
  sourceId: string;
  sourceLayer: string;
  featureId: string | number;
}

export interface SidebarSection {
  // Unique per emitted section: `${layerId}::${stableFeatureId}` for utility/parcel
  // sections (one per distinct feature), `${layerId}::${category}`-deduped custom-data
  // layer id for custom-data sections (grouped per category, not per feature — see below).
  id: string;
  // The layer id (utility sections) or custom-data layer id (custom-data sections).
  // Kept separate from `id` because analytics keys off the layer, not the per-feature id.
  layerId: string;
  title: string;
  categoryLabel: string | null;
  isCustomData: boolean;
  // The single feature's geometry (utility/parcel sections), used later for hover
  // highlight. Null for custom-data sections (hover-highlight is out of scope there).
  geometry: GeoJSON.Geometry | null;
  // Empty when a layer matched but none of its properties had a displayable field —
  // the sidebar shell renders the "Detailed attributes are not available for this layer." fallback.
  fields: SidebarFieldRow[];
  color?: string;
  // Exact on-map rgb() color for the hover/select highlight core, read per-geometry from the
  // feature's evaluated paint (not the preset-snapped `color`, which stays for copy-to-canvas).
  coreColor?: string;
  minZoom?: number;
  fieldsToCopy?: { field: string; value: string }[];
  // Custom-data sections only: de-duplicated, humanized `CateTitle`s (display).
  customCategories?: string[];
  // Custom-data sections only: same de-duplicated set as `customCategories`, but RAW
  // (un-humanized) `CateTitle` strings, 1:1 in order. Analytics must use these — not the
  // humanized display strings — so the PostHog `category` dimension stays a stable format.
  customCategoriesRaw?: string[];
  // Present only for vector-tile features with a promoted id; see FeatureRef.
  featureRef?: FeatureRef;
}

export interface BuildSidebarSectionsContext {
  tilesConfig: TilesConfig | undefined;
  toc: TOCType;
  legendParentMap: Map<string, string>;
  includeFieldsToCopy: boolean;
  // Display names for custom-data categories, keyed `${layerId}::${CateTitle}`; absent entries
  // humanize.
  customDataCategoryLabels?: Map<string, string>;
}

// Surfaced per-feature build failures so the sidebar shell can fire the
// `mapMetadataLayerError` telemetry (this module stays pure/JSX-free).
export interface MetadataBuildError {
  layerId: string;
  configKey: string;
  message: string;
}

export interface BuildSidebarResult {
  sections: SidebarSection[];
  errors: MetadataBuildError[];
}

interface SectionAccumulator {
  config: MapboxSourceConfig;
  fields: SidebarFieldRow[];
  fieldsToCopy: { field: string; value: string }[];
  geometry: GeoJSON.Geometry | null;
  color?: [number, number, number, number];
  coreColor?: string;
  featureRef?: FeatureRef;
}

// The subset of a feature's properties that will actually render as a row (matching
// field, not excluded) — built once per feature and reused both for the rows themselves
// and, when the layer has no id-type field, as the input to the content-hash fallback.
interface IncludedFieldEntry {
  key: string;
  field: MapboxSourceConfig['fields'][string];
  rawValue: any;
  displayValue: any;
}

// stableFeatureId: a value that identifies the SAME real-world feature across adjacent
// tile fragments (which return duplicate rows with identical properties for a feature
// straddling a tile boundary), so those fragments merge into one section instead of two.
// 1. Parcels always carry `robust_id` (excluded from display, but present on properties).
// 2. Other layers: the single field whose config type is 'id'.
// 3. No id field on the layer: hash the included/displayed values (sorted by key) — this
//    merges identical fragments while still giving genuinely distinct features (different
//    attribute values) their own section.
function computeStableFeatureId(
  properties: Record<string, any>,
  config: MapboxSourceConfig,
  isParcel: boolean,
  includedEntries: IncludedFieldEntry[],
): string {
  if (isParcel) return String(properties[REPORT_ALL_ID]);

  const idFieldKey = Object.keys(config.fields ?? {}).find((k) => config.fields?.[k]?.type === 'id');
  const idValue = idFieldKey ? properties[idFieldKey] : undefined;
  if (idValue !== null && idValue !== undefined && idValue !== '') return String(idValue);

  const sortedByKey = [...includedEntries].sort((a, b) => a.key.localeCompare(b.key));
  const displayValuesByKey: Record<string, any> = {};
  for (const { key, displayValue } of sortedByKey) displayValuesByKey[key] = displayValue;
  return JSON.stringify(displayValuesByKey);
}

// The identity of a single queried feature: the stable section key it maps to plus the
// pieces buildSidebarSections needs to render it. `sectionKey` is byte-identical to the
// `SidebarSection.id` this feature produces, so a left-click can single-select exactly the
// section the sidebar builds. `null` when the feature can't map to a section (no properties,
// or no tilesConfig entry). `config`/`includedEntries` are empty for custom-data features.
export interface FeatureIdentity {
  sectionKey: string;
  config: MapboxSourceConfig | undefined;
  isParcel: boolean;
  isCustomData: boolean;
  includedEntries: IncludedFieldEntry[];
}

// Single source of truth for a feature's section identity, shared by the click handler
// (which key to single-select) and buildSidebarSections (each section's id), so the two
// can never drift.
export function deriveFeatureIdentity(
  feature: MapGeoJSONFeature,
  tilesConfig: TilesConfig | undefined,
): FeatureIdentity | null {
  if (!feature.properties) return null;

  if (isCustomDataFeature(feature)) {
    // Custom-data lines have no tilesConfig entry; their section id is the layer id.
    return {
      sectionKey: customDataFeatureLayerId(feature),
      config: undefined,
      isParcel: false,
      isCustomData: true,
      includedEntries: [],
    };
  }

  const isParcel = isParcelFeature(feature);
  const configKey = isParcel ? feature.source : feature.properties.cgSourceId;
  const config = tilesConfig?.[configKey];
  if (!config) return null;

  const includedEntries: IncludedFieldEntry[] = [];
  forEach(feature.properties, (v, k) => {
    const field = isParcel ? formatParcelField(k) : config.fields?.[k];
    if (!field || EXCLUDED_FIELDS.includes(k)) return;
    const displayValue = isParcel && ZIP_FIELD_KEYS.has(k) ? normalizeZip(v) : v;
    includedEntries.push({ key: k, field, rawValue: v, displayValue });
  });

  const stableFeatureId = computeStableFeatureId(feature.properties, config, isParcel, includedEntries);
  return {
    sectionKey: `${config.id}::${stableFeatureId}`,
    config,
    isParcel,
    isCustomData: false,
    includedEntries,
  };
}

// Multiplies the raw 0-1 paint color channels by 255 before matching a preset;
// getNearestPresetColor already defaults to '#000000' when given `undefined`.
function resolveSectionColor(raw: [number, number, number, number] | undefined): string {
  if (!raw) return getNearestPresetColor();
  const [r, g, b, a] = raw;
  return getNearestPresetColor([r * 255 || 0, g * 255 || 0, b * 255 || 0, a * 255 || 1]);
}

// The exact on-map color for the highlight core, read per-geometry from the feature's
// mapbox-evaluated paint (queryRenderedFeatures pre-evaluates, so this is the rendered color).
// Alpha is dropped — the highlight layers apply their own opacity. Undefined when the feature has
// no usable color (icon/symbol points, pattern fills, empty paint, transparent), so CORE_COLOR
// falls back to blue-80.
function resolveCoreColor(
  paint: Record<string, any> | undefined,
  geometryType: GeoJSON.Geometry['type'] | undefined,
): string | undefined {
  if (!paint || !geometryType) return undefined;
  let key: string;
  switch (geometryType) {
    case 'Point':
    case 'MultiPoint':
      key = 'circle-color';
      break;
    case 'LineString':
    case 'MultiLineString':
      key = 'line-color';
      break;
    case 'Polygon':
    case 'MultiPolygon':
      key = 'fill-color';
      break;
    default:
      return undefined;
  }
  const c = paint[key];
  if (!c || typeof c !== 'object') return undefined;
  const { r, g, b, a } = c as { r?: number; g?: number; b?: number; a?: number };
  if (![r, g, b].every((n) => typeof n === 'number' && Number.isFinite(n))) return undefined;
  if (typeof a === 'number' && a <= 0) return undefined;
  return `rgb(${Math.round((r as number) * 255)}, ${Math.round((g as number) * 255)}, ${Math.round((b as number) * 255)})`;
}

// Stable partition: filled rows first, then empty rows, each keeping source order.
// Clusters em-dash rows at the bottom of a section.
function orderRowsEmptyLast(rows: SidebarFieldRow[]): SidebarFieldRow[] {
  return [...rows.filter((r) => !r.isEmpty), ...rows.filter((r) => r.isEmpty)];
}

/**
 * Groups, dedupes and skips the queried features into `SidebarSection[]` as pure data
 * so it can be unit-tested and reused by the metadata sidebar shell.
 */
export function buildSidebarSections(
  features: MapGeoJSONFeature[],
  ctx: BuildSidebarSectionsContext,
): BuildSidebarResult {
  const { tilesConfig, toc, legendParentMap, includeFieldsToCopy, customDataCategoryLabels } = ctx;

  const sections: Record<string, SectionAccumulator> = {};
  // Custom-data lines have no tilesConfig entry and carry only `CateTitle`, so they're
  // grouped separately.
  const customDataSections: Record<string, { categories: string[] }> = {};
  const customDataSeen = new Set<string>();
  const errors: MetadataBuildError[] = [];

  features.forEach((f) => {
    if (!f.properties) return;

    // Custom-data lines are grouped separately via narrow, non-throwing helpers, outside the try.
    if (isCustomDataFeature(f)) {
      const layerId = customDataFeatureLayerId(f);
      const category = String(f.properties.CateTitle ?? '');
      const dedupeKey = `${layerId}::${category}`;
      if (customDataSeen.has(dedupeKey)) return;
      customDataSeen.add(dedupeKey);
      const section = customDataSections[layerId] ?? { categories: [] };
      section.categories.push(category);
      customDataSections[layerId] = section;
      return;
    }

    const isParcel = isParcelFeature(f);
    const configKey = isParcel ? f.source : f.properties.cgSourceId;
    const config = tilesConfig?.[configKey];
    if (!config) return;

    // Isolate per-feature failures so one malformed layer can't blank the whole sidebar.
    // deriveFeatureIdentity (which builds includedEntries + the stable id) runs INSIDE the
    // try so a throw there is caught per-feature, exactly like the acc build below.
    try {
      const identity = deriveFeatureIdentity(f, tilesConfig);
      // config exists (checked above), so identity is non-null and non-custom here; this
      // null-check is only for the type checker before destructuring.
      if (!identity) return;
      const { includedEntries, sectionKey } = identity;
      // Adjacent tiles return duplicate fragments of the same feature with identical
      // properties — the first fragment's data is representative, so later ones merge
      // in by being skipped rather than re-processed. Keeping only the first fragment's
      // geometry is intentional: the on-map hover glow traces that first tile-clipped slice
      // near the click (not the full multi-tile feature), which is fine because the identify
      // query is a 10px box — a near-click affordance.
      if (sections[sectionKey]) return;

      const acc: SectionAccumulator = { config, fields: [], fieldsToCopy: [], geometry: f.geometry ?? null };

      const layerPaint = (f.layer as { paint?: Record<string, any> } | undefined)?.paint;
      const lineColor = layerPaint?.['line-color'];
      if (lineColor) {
        const { r, g, b, a } = lineColor;
        acc.color = [r, g, b, a];
      }
      acc.coreColor = resolveCoreColor(layerPaint, f.geometry?.type);

      // Feature-state highlight target. `f.id` is the tile-promoted id; absent ids fall back to
      // the fragment geometry above, so this is set only when all three parts exist.
      if (f.id !== undefined && f.id !== null && f.source && f.sourceLayer) {
        acc.featureRef = { sourceId: f.source, sourceLayer: f.sourceLayer, featureId: f.id };
      }

      includedEntries.forEach(({ key: k, field, rawValue, displayValue }) => {
        const { label, value, isUrl } = formatFieldRow(field, displayValue);
        // Sidebar-only: treat missing raw values as empty and render an em-dash. A real
        // boolean `false` is a genuine value, so it's never empty.
        const isEmpty =
          typeof rawValue !== 'boolean' && (rawValue == null || rawValue === '' || rawValue === 'Not Available');
        // formatFieldRow blanks booleans (common.ts); the sidebar renders them as Yes/No instead.
        const booleanText = typeof rawValue === 'boolean' ? (rawValue ? 'Yes' : 'No') : undefined;

        acc.fields.push({
          label,
          value: isEmpty ? '—' : (booleanText ?? value),
          isUrl,
          isEmpty,
          uniqueId: `${config.id}.${k}`,
          eyeSupported: isSupportedMetadataLayer(config.id),
        });

        if (includeFieldsToCopy) {
          // Copy uses the RAW value (not the zip-normalized displayValue) for every field type
          // except date, which is formatted.
          const { label: copyLabel, value: formattedValue } = formatFieldRow(field, rawValue);
          const copyValue = booleanText ?? (field.type === 'date' ? formattedValue : rawValue);
          acc.fieldsToCopy.push({ field: copyLabel, value: copyValue });
        }
      });

      sections[sectionKey] = acc;
    } catch (err) {
      console.error(`Failed to build metadata sidebar section for layer ${config.id}`, err);
      errors.push({
        layerId: config.id,
        configKey,
        message: err instanceof Error ? err.message : String(err),
      });
    }
  });

  const result: SidebarSection[] = [];

  forEach(sections, (acc, key) => {
    const layerId = acc.config.id;
    result.push({
      id: key,
      layerId,
      title: acc.config.name,
      categoryLabel: deriveCategoryLabel({ layerId, isCustomData: false, legendParentMap }),
      isCustomData: false,
      geometry: acc.geometry,
      fields: orderRowsEmptyLast(acc.fields),
      color: resolveSectionColor(acc.color),
      coreColor: acc.coreColor,
      ...(acc.featureRef ? { featureRef: acc.featureRef } : {}),
      minZoom: acc.config.minZoom ?? 17,
      ...(includeFieldsToCopy ? { fieldsToCopy: acc.fieldsToCopy } : {}),
    });
  });

  // Render custom-data groups into the same section list. Header label = friendly TOC
  // label (falls back to "Custom Data"); one display name per distinct `CateTitle`.
  // Bypasses deriveCategoryLabel's `!cateTitle -> null` guard so an empty or missing
  // CateTitle still renders as an "Uncategorized" row.
  forEach(customDataSections, (section, layerId) => {
    // `section.categories` is already de-duped on the RAW CateTitle string (see the dedupeKey
    // above), so it doubles as the raw analytics array; name it for display, 1:1 in order.
    const customCategoriesRaw: string[] = section.categories;
    const customCategories: string[] = customCategoriesRaw.map((category) =>
      customDataCategoryLabel(customDataCategoryLabels, layerId, category),
    );

    result.push({
      id: layerId,
      layerId,
      title: customDataLayerLabel(toc, layerId),
      categoryLabel: null,
      isCustomData: true,
      geometry: null,
      fields: [],
      customCategories,
      customCategoriesRaw,
    });
  });

  return { sections: result, errors };
}
